import { useMemo, memo, useState } from 'react'
import {
  Box,
  Typography,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material'
import GridViewIcon from '@mui/icons-material/GridView'
import BarChartIcon from '@mui/icons-material/BarChart'
import DashboardIcon from '@mui/icons-material/Dashboard'
import TableChartIcon from '@mui/icons-material/TableChart'
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen'
import GroupsIcon from '@mui/icons-material/Groups'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import { useUtilisationData } from './Utilisation.data'
import { SchChip, TOKENS, TYPE, FONT_MONO } from './SchedulerDesign'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { generateConstraintRows } from 'Features/SchedulerView/GenerateConstraintRows'
import {
  SchedulerPackagingRoutesRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
  GanttResourceData,
} from './Scheduler.types'
import {
  AssetIntervals,
  alignRowsToResources,
  deriveAssetIntervals,
} from './ResourceUsage.hourly'

// ─── Constants ─────────────────────────────────────────────────────────────────

const NB = 28 // 7 days × 4 six-hour buckets
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

type AssetKind = 'kitchen' | 'line' | 'crew' | 'cip'

const KIND_ICON: Record<AssetKind, typeof SoupKitchenIcon> = {
  kitchen: SoupKitchenIcon,
  line: PrecisionManufacturingIcon,
  crew: GroupsIcon,
  cip: CleaningServicesIcon,
}

// ─── heatColor — sequential ramp ported from OutputShared.jsx ─────────────────
// idle → pale blue → mid blue → deep blue

const HEAT_STOPS: [number, [number, number, number]][] = [
  [0, [220, 234, 246]],
  [0.4, [140, 192, 224]],
  [0.7, [62, 141, 214]],
  [1, [19, 80, 124]],
]

function heatColor(u: number): string {
  if (u <= 0.001) return TOKENS.ganttIdle
  let a = HEAT_STOPS[0]
  let b = HEAT_STOPS[HEAT_STOPS.length - 1]
  for (let i = 0; i < HEAT_STOPS.length - 1; i++) {
    if (u >= HEAT_STOPS[i][0] && u <= HEAT_STOPS[i + 1][0]) {
      a = HEAT_STOPS[i]
      b = HEAT_STOPS[i + 1]
      break
    }
  }
  const t = (u - a[0]) / (b[0] - a[0] || 1)
  const c = a[1].map((ca, i) => Math.round(ca + (b[1][i] - ca) * t))
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

// ─── Data shapes ──────────────────────────────────────────────────────────────

type RowData = {
  name: string
  sub?: string
  u: number[] // 28 values [0..1] — utilisation per 6h bucket
  over: boolean[] // 28 flags — true → red ring (crews / constraint breaches only)
  avg: number // [0..1]
  peak: number // [0..1]
  kind: AssetKind
  group: string
  // Actual value used per day (hours), aligned to day_of_week 0..6 — for
  // crews (no daily breakdown available) this repeats the same overall
  // total across all 7 days.
  dayUsed: number[]
  // Actual capacity per day (hours) that dayUsed is measured against.
  dayCapacity: number[]
  // Kitchens only: what base material(s) actually ran that day and their
  // rate (kitchen capacity varies by base material, so there's no single
  // fixed number to show per kitchen — this is shown per day in the
  // tooltip instead of as a fixed left-hand label).
  dayNote?: string[]
}

type HeatGroup = {
  label: string
  icon: AssetKind
  rows: RowData[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dailyTo28(daily7: number[]): number[] {
  const out: number[] = []
  for (let d = 0; d < 7; d++) {
    const v = daily7[d] ?? 0
    for (let b = 0; b < 4; b++) out.push(v)
  }
  return out
}

function overTo28(over7: boolean[]): boolean[] {
  const out: boolean[] = []
  for (let d = 0; d < 7; d++) {
    const v = over7[d] ?? false
    for (let b = 0; b < 4; b++) out.push(v)
  }
  return out
}

/** "DPL03 50% · DPL05 30%": lines with a non-trivial share, largest first,
 *  prefixed "lines:"; '' when no line drew anything. */
function lineBreakdown(shares: { name: string; share: number }[]): string {
  const parts = shares
    .filter((s) => s.share >= 0.005)
    .sort((a, b) => b.share - a.share)
    .map((s) => `${s.name} ${Math.round(s.share * 100)}%`)
  return parts.length ? `lines: ${parts.join(' · ')}` : ''
}

/** Per-day tooltip notes from two sources, joined where both have text. */
function joinDayNotes(a?: string[], b?: string[]): string[] | undefined {
  if (!a && !b) return undefined
  return Array.from({ length: 7 }, (_, d) =>
    [a?.[d], b?.[d]].filter(Boolean).join(' · ')
  )
}

// ─── Hourly table — true 1h-resolution numbers, computed client-side from ──
// the raw schedule (outputRows) rather than the day-level BaseUtilisation
// aggregate the Heat/Bars views above use. This is what lets every resource
// (kitchens, CIP, crew, and named material constraints like "sugar") show an
// exact number per hour instead of a 6h-bucket average.

const HOURS = 168
const HOUR_COL_W = 26
const NAME_COL_W = 210
const HOURLY_ROW_H = 22
const HOURLY_HEADER_ROW_H = 18

type HourlyResourceRow = {
  name: string
  sub?: string
  kind: AssetKind
  group: string
  /** Amount consumed each hour — a 0..1 busy-fraction for kitchen/CIP assets,
   *  raw units (e.g. kg) for named resource constraints. */
  used: number[]
  /** Denominator for `used` each hour — 1 (or less, if downtime eats into
   *  that hour) for assets; the constraint's constant capacity for resources. */
  cap: number[]
  /** Extra hover text per hour — for a kitchen, which lines drew its
   *  capacity and how much each (e.g. "DPL03 50% · DPL05 30%"). */
  hourNote?: string[]
}

/** Sum of overlap (in hours, fractional) between `events` and each whole
 * hour in `[0, hours)`. Mirrors the Rust engine's per-hour overlap logic
 * (utilisation.rs::sum_overlap) but at 1h resolution instead of 24h. */
function sumOverlapPerHour(
  events: { start: number; end: number }[],
  hours: number
): number[] {
  const out = new Array(hours).fill(0)
  for (const { start, end } of events) {
    if (end <= start) continue
    const s = Math.max(0, Math.floor(start))
    const e = Math.min(hours, Math.ceil(end))
    for (let h = s; h < e; h++) {
      const lo = Math.max(start, h)
      const hi = Math.min(end, h + 1)
      if (hi > lo) out[h] += hi - lo
    }
  }
  return out
}

function buildAssetHourlyRow(
  r: AssetIntervals,
  kind: 'kitchen' | 'line' | 'cip',
  group: string
): HourlyResourceRow {
  let events = [...r.productionBars, ...r.cleaningBars]
  if (kind !== 'cip') {
    // A kitchen batch feeding two lines arrives as two bars with the same
    // window; summed, the kitchen would read 200% busy. A CIP is left alone
    // because two assets cleaning on it at once is a real double booking.
    const seen = new Set<string>()
    events = events.filter((e) => {
      const k = `${e.start}|${e.end}`
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }
  const used = sumOverlapPerHour(events, HOURS)
  const downBusy = sumOverlapPerHour(r.downtimeIntervals, HOURS)
  const cap = downBusy.map((v) => Math.max(0, 1 - v))
  // An asset's capacity limit is time: one hour of runtime per hour, less
  // whatever downtime eats into it. Summed over the horizon that is a single
  // honest number to show next to the name. The per-base-material kitchen run
  // rate is not — it changes with whatever the kitchen is running, so it stays
  // in the per-day tooltip rather than becoming a fixed label.
  const availHrs = cap.reduce((sum, v) => sum + v, 0)
  return {
    name: r.name,
    sub: `cap ${Math.round(availHrs)}h`,
    kind,
    group,
    used,
    cap,
  }
}

/** How close to `cap` counts as hitting the limit. Assets are measured in
 *  fractions of an hour and resources in raw units, so this is deliberately
 *  tight — it absorbs float noise from the overlap sums and nothing else. */
const AT_LIMIT_EPS = 1e-6

/** Rounded display value + heat fraction + at-limit / over-limit flags for one
 *  cell. `atLimit` is what draws the red outline, and it is a superset of
 *  `over`: an hour that exactly consumes the capacity is just as binding as one
 *  that exceeds it — and for an asset, whose capacity IS its availability,
 *  reaching the cap is the only way to be at the limit at all. */
function hourlyCellInfo(
  kind: AssetKind,
  used: number,
  cap: number
): { text: string; frac: number; atLimit: boolean; over: boolean } {
  const atLimit = cap > 0 && used >= cap - AT_LIMIT_EPS
  const over = cap > 0 && used > cap + AT_LIMIT_EPS
  if (kind === 'crew') {
    const frac = cap > 0 ? used / cap : used > 0 ? 1 : 0
    return {
      text: used > 0.05 ? used.toFixed(1) : '',
      frac: Math.min(1, frac),
      atLimit,
      over,
    }
  }
  const frac = cap > 0 ? Math.min(1, used / cap) : 0
  const pct = Math.round(frac * 100)
  return { text: pct > 0 ? `${pct}` : '', frac, atLimit, over }
}

// ─── Primitives ───────────────────────────────────────────────────────────────

const OCap = memo(
  ({
    value,
    peak,
    over,
    height = 8,
  }: {
    value: number
    peak: number
    over: boolean
    height?: number
  }) => {
    const col = over
      ? TOKENS.ganttDowntime
      : value > 0.8
        ? TOKENS.ganttClean
        : TOKENS.ganttProduction
    return (
      <Box
        sx={{
          position: 'relative',
          height,
          bgcolor: TOKENS.ganttIdle,
          borderRadius: height / 2,
          overflow: 'hidden',
          border: `1px solid ${TOKENS.line200}`,
        }}
      >
        <Box
          sx={{
            width: `${Math.max(0, Math.min(1, value)) * 100}%`,
            height: '100%',
            bgcolor: col,
            borderRadius: height / 2,
          }}
        />
        {peak > value && (
          <Tooltip title={`peak ${Math.round(peak * 100)}%`} arrow>
            <Box
              sx={{
                position: 'absolute',
                left: `${Math.min(1, peak) * 100}%`,
                top: -1,
                bottom: -1,
                width: 2,
                bgcolor: TOKENS.ink500,
                opacity: 0.55,
                cursor: 'default',
              }}
            />
          </Tooltip>
        )}
      </Box>
    )
  }
)
OCap.displayName = 'OCap'

type LegendProps = { overLabel?: string }

const HeatScaleLegend = memo(({ overLabel = 'over capacity' }: LegendProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Typography sx={{ fontSize: 9.5, color: TOKENS.ink400, fontWeight: 600 }}>
      0%
    </Typography>
    <Box
      sx={{
        width: 132,
        height: 9,
        borderRadius: 5,
        border: `1px solid ${TOKENS.line200}`,
        background: `linear-gradient(90deg, ${heatColor(0.02)}, ${heatColor(0.4)}, ${heatColor(0.7)}, ${heatColor(1)})`,
      }}
    />
    <Typography sx={{ fontSize: 9.5, color: TOKENS.ink400, fontWeight: 600 }}>
      100%
    </Typography>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        ml: '8px',
      }}
    >
      <Box
        sx={{
          width: 11,
          height: 11,
          borderRadius: '2px',
          bgcolor: heatColor(0.6),
          boxShadow: `inset 0 0 0 2px ${TOKENS.ganttDowntime}`,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: 9.5, color: TOKENS.ink400, fontWeight: 600 }}>
        {overLabel}
      </Typography>
    </Box>
  </Box>
))
HeatScaleLegend.displayName = 'HeatScaleLegend'

const HeatDayHeader = memo(() => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: '5px' }}>
    <Box sx={{ width: 158, minWidth: 158 }} />
    <Box sx={{ flex: 1, display: 'flex', gap: '1.5px' }}>
      {DAY_SHORT.map((d, di) => (
        <Box
          key={d}
          sx={{
            flex: 4,
            ml: di > 0 ? '4px' : 0,
            textAlign: 'center',
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: TOKENS.ink400,
            borderBottom: `1px solid ${TOKENS.line200}`,
            pb: '2px',
          }}
        >
          {d}
        </Box>
      ))}
    </Box>
    <Typography
      sx={{
        width: 42,
        textAlign: 'right',
        fontSize: 8.5,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: TOKENS.ink300,
      }}
    >
      avg
    </Typography>
  </Box>
))
HeatDayHeader.displayName = 'HeatDayHeader'

const HeatRow = memo(
  ({
    name,
    sub,
    u,
    over,
    dayUsed,
    dayCapacity,
    dayNote,
  }: {
    name: string
    sub?: string
    u: number[]
    over: boolean[]
    dayUsed: number[]
    dayCapacity: number[]
    dayNote?: string[]
  }) => {
    const avg = u.reduce((s, x) => s + x, 0) / u.length
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: 26 }}>
        <Box
          sx={{
            width: 158,
            minWidth: 158,
            pr: '12px',
            display: 'flex',
            alignItems: 'baseline',
            gap: '6px',
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              color: TOKENS.ink700,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </Typography>
          {sub && (
            <Typography
              sx={{
                fontSize: 9,
                color: TOKENS.ink300,
                fontFamily: FONT_MONO,
                flexShrink: 0,
              }}
            >
              {sub}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', gap: '1.5px' }}>
          {u.map((v, bi) => {
            const day = Math.floor(bi / 4)
            const used = dayUsed[day] ?? 0
            const cap = dayCapacity[day] ?? 0
            const note = dayNote?.[day]
            return (
              <Tooltip
                key={bi}
                title={`${DAY_SHORT[day]} ${String((bi % 4) * 6).padStart(2, '0')}:00 · ${Math.round(v * 100)}% · ${used.toFixed(1)}h / ${cap.toFixed(1)}h${note ? ' · ' + note : ''}${over[bi] ? ' · over capacity' : ''}`}
                arrow
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 18,
                    borderRadius: '1.5px',
                    bgcolor: heatColor(v),
                    ml: bi % 4 === 0 && bi > 0 ? '4px' : 0,
                    boxShadow: over[bi]
                      ? `inset 0 0 0 2px ${TOKENS.ganttDowntime}`
                      : 'none',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
            )
          })}
        </Box>
        <Typography
          sx={{
            width: 42,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 10.5,
            fontWeight: 600,
            color: TOKENS.ink500,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {Math.round(avg * 100)}%
        </Typography>
      </Box>
    )
  }
)
HeatRow.displayName = 'HeatRow'

const BarRow = memo(({ row }: { row: RowData }) => {
  const isOver = row.over.some(Boolean)
  const Icon = KIND_ICON[row.kind]
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        height: 32,
        borderBottom: `1px solid ${TOKENS.line100}`,
      }}
    >
      <Box
        sx={{
          width: 26,
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 15, color: TOKENS.ink400 }} />
      </Box>
      <Box
        sx={{
          width: 150,
          minWidth: 150,
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1.2,
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: TOKENS.ink900,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.name}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '6px',
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              fontSize: 9.5,
              color: TOKENS.ink300,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {row.group}
          </Typography>
          {row.sub && (
            <Typography
              sx={{
                fontSize: 9,
                color: TOKENS.ink400,
                fontFamily: FONT_MONO,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {row.sub}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 80 }}>
        <OCap value={row.avg} peak={row.peak} over={isOver} height={9} />
      </Box>
      <Typography
        sx={{
          width: 46,
          textAlign: 'right',
          fontFamily: FONT_MONO,
          fontSize: 13,
          fontWeight: 600,
          color: isOver ? TOKENS.ganttDowntime : TOKENS.ink900,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {Math.round(row.avg * 100)}%
      </Typography>
      <Typography
        sx={{
          width: 70,
          fontSize: 10,
          color: TOKENS.ink400,
          fontFamily: FONT_MONO,
          flexShrink: 0,
        }}
      >
        peak {Math.round(row.peak * 100)}%
      </Typography>
      <Box sx={{ width: 72, flexShrink: 0 }}>
        {isOver && (
          <SchChip tone="failed" icon={<PriorityHighIcon />}>
            over
          </SchChip>
        )}
      </Box>
    </Box>
  )
})
BarRow.displayName = 'BarRow'

type ViewMode = 'heat' | 'bars' | 'both' | 'hourly'

const ViewToggle = memo(
  ({
    value,
    onChange,
  }: {
    value: ViewMode
    onChange: (v: ViewMode) => void
  }) => (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, v: ViewMode | null) => v && onChange(v)}
      size="small"
      sx={{
        '& .MuiToggleButton-root': {
          height: 28,
          px: '11px',
          fontSize: 11.5,
          fontWeight: 600,
          textTransform: 'none',
          gap: '5px',
          color: TOKENS.ink600,
          borderColor: TOKENS.line300,
          '&.Mui-selected': {
            bgcolor: TOKENS.stateComplete,
            color: '#fff',
            borderColor: TOKENS.stateComplete,
            '&:hover': { bgcolor: TOKENS.stateComplete },
          },
          '& svg': { fontSize: 14 },
        },
      }}
    >
      <ToggleButton value="heat">
        <GridViewIcon />
        Heatmap
      </ToggleButton>
      <ToggleButton value="bars">
        <BarChartIcon />
        Bars
      </ToggleButton>
      <ToggleButton value="both">
        <DashboardIcon />
        Both
      </ToggleButton>
      <ToggleButton value="hourly">
        <TableChartIcon />
        Hourly
      </ToggleButton>
    </ToggleButtonGroup>
  )
)
ViewToggle.displayName = 'ViewToggle'

// ─── Hourly table ────────────────────────────────────────────────────────────

const HourlyHeader = memo(() => {
  const cells: React.ReactNode[] = []
  for (let d = 0; d < 7; d++) {
    const startCol = 2 + d * 24
    cells.push(
      <div
        key={`d-${d}`}
        style={{
          gridRow: 1,
          gridColumn: `${startCol} / span 24`,
          height: HOURLY_HEADER_ROW_H,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: TOKENS.ink500,
          borderBottom: `1px solid ${TOKENS.line200}`,
          borderLeft: d > 0 ? `1px solid ${TOKENS.line300}` : 'none',
          position: 'sticky',
          top: 0,
          background: TOKENS.surface50,
          zIndex: 3,
        }}
      >
        {DAY_SHORT[d]}
      </div>
    )
  }
  for (let h = 0; h < HOURS; h++) {
    const hourOfDay = h % 24
    cells.push(
      <div
        key={`h-${h}`}
        style={{
          gridRow: 2,
          gridColumn: h + 2,
          height: HOURLY_HEADER_ROW_H,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONT_MONO,
          fontSize: 8,
          fontWeight: 600,
          color: TOKENS.ink400,
          borderLeft:
            hourOfDay === 0
              ? `1px solid ${TOKENS.line300}`
              : `1px solid ${TOKENS.line100}`,
          position: 'sticky',
          top: HOURLY_HEADER_ROW_H,
          background: TOKENS.surface50,
          zIndex: 3,
        }}
      >
        {String(hourOfDay).padStart(2, '0')}
      </div>
    )
  }
  return (
    <>
      <div
        style={{
          position: 'sticky',
          left: 0,
          top: 0,
          gridRow: '1 / 3',
          gridColumn: 1,
          background: TOKENS.surface50,
          borderBottom: `1px solid ${TOKENS.line200}`,
          borderRight: `1px solid ${TOKENS.line200}`,
          zIndex: 4,
        }}
      />
      {cells}
    </>
  )
})
HourlyHeader.displayName = 'HourlyHeader'

const HourlyGroupHeader = memo(
  ({ label, Icon }: { label: string; Icon: typeof SoupKitchenIcon }) => (
    <div
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 24,
        paddingLeft: 4,
        background: TOKENS.surface0,
      }}
    >
      <Icon sx={{ fontSize: 13, color: TOKENS.stateComplete }} />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: TOKENS.ink500,
        }}
      >
        {label}
      </Typography>
      <div style={{ flex: 1, height: 1, background: TOKENS.line100 }} />
    </div>
  )
)
HourlyGroupHeader.displayName = 'HourlyGroupHeader'

const HourlyRowLine = memo(({ row }: { row: HourlyResourceRow }) => {
  const cells: React.ReactNode[] = []
  for (let h = 0; h < HOURS; h++) {
    const { text, frac, atLimit, over } = hourlyCellInfo(
      row.kind,
      row.used[h],
      row.cap[h]
    )
    const day = Math.floor(h / 24)
    const hourOfDay = h % 24
    const limitNote = over
      ? ' · over capacity'
      : atLimit
        ? ' · at capacity'
        : ''
    const note = row.hourNote?.[h]
    const title = `${DAY_SHORT[day]} ${String(hourOfDay).padStart(2, '0')}:00 · ${row.used[h].toFixed(2)} / ${row.cap[h].toFixed(2)}${limitNote}${note ? ' · ' + note : ''}`
    cells.push(
      <div
        key={h}
        title={title}
        style={{
          height: HOURLY_ROW_H,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONT_MONO,
          fontSize: 8,
          fontWeight: 600,
          color: frac > 0.55 ? '#fff' : TOKENS.ink600,
          background: heatColor(frac),
          boxShadow: atLimit
            ? `inset 0 0 0 1.5px ${TOKENS.ganttDowntime}`
            : 'none',
          borderLeft:
            hourOfDay === 0
              ? `1px solid ${TOKENS.line300}`
              : `1px solid rgba(255,255,255,0.4)`,
        }}
      >
        {text}
      </div>
    )
  }
  return (
    <>
      <div
        style={{
          position: 'sticky',
          left: 0,
          gridColumn: 1,
          height: HOURLY_ROW_H,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          paddingLeft: 6,
          paddingRight: 6,
          background: TOKENS.surface0,
          borderRight: `1px solid ${TOKENS.line200}`,
          borderBottom: `1px solid ${TOKENS.line100}`,
          overflow: 'hidden',
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 10.5,
            color: TOKENS.ink700,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // Flex child: without this the name refuses to shrink and pushes
            // the capacity label out of the clipped sticky column.
            minWidth: 0,
          }}
        >
          {row.name}
        </Typography>
        {/* The capacity every cell on this row is measured against — the
            denominator behind the numbers, and what the red outlines mean. */}
        {row.sub && (
          <Typography
            sx={{
              fontSize: 8.5,
              color: TOKENS.ink400,
              fontFamily: FONT_MONO,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              marginLeft: 'auto',
            }}
          >
            {row.sub}
          </Typography>
        )}
      </div>
      {cells}
    </>
  )
})
HourlyRowLine.displayName = 'HourlyRowLine'

type HourlyGroup = {
  label: string
  icon: AssetKind
  rows: HourlyResourceRow[]
}

const HourlyTable = memo(({ groups }: { groups: HourlyGroup[] }) => (
  <Box
    sx={{
      overflow: 'auto',
      border: `1px solid ${TOKENS.line200}`,
      borderRadius: '4px',
      maxHeight: 560,
    }}
  >
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${NAME_COL_W}px repeat(${HOURS}, ${HOUR_COL_W}px)`,
        gridAutoRows: 'min-content',
        width: NAME_COL_W + HOURS * HOUR_COL_W,
      }}
    >
      <HourlyHeader />
      {groups.map((g) => (
        <HourlyGroupBlock key={g.label} group={g} />
      ))}
    </div>
  </Box>
))
HourlyTable.displayName = 'HourlyTable'

const HourlyGroupBlock = memo(({ group }: { group: HourlyGroup }) => (
  <>
    <HourlyGroupHeader label={group.label} Icon={KIND_ICON[group.icon]} />
    {group.rows.map((r) => (
      <HourlyRowLine key={r.name} row={r} />
    ))}
  </>
))
HourlyGroupBlock.displayName = 'HourlyGroupBlock'

// ─── Main Component ───────────────────────────────────────────────────────────

export const ScheduleResourceUsageView = ({
  scheduleId,
  iteration,
  selectedWeek,
  outputRows = [],
  packagingRoutes = [],
  kitchenResources = [],
  cipResources = [],
  constraints = [],
}: {
  scheduleId: string
  iteration: string
  selectedWeek?: number
  outputRows?: InputRow[]
  packagingRoutes?: SchedulerPackagingRoutesRowType[]
  /** Already-built kitchen/CIP Gantt resources (from ScheduleFullGantt.view.tsx)
   *  — reused here so the hourly table doesn't re-derive productive/cleaning
   *  windows or multi-week time-shifting a second time. */
  kitchenResources?: GanttResourceData[]
  cipResources?: GanttResourceData[]
  constraints?: SchedulerResourceAndMaterialConstraintsRowType[]
}) => {
  // Hourly is the default view: it is the only one at the resolution the
  // schedule is actually built at, and the only one that shows each
  // resource's capacity limit and rings the hours that hit it. `null` means
  // "not chosen yet", so the default can fall back to Heat+Bars on a host
  // that passes no hourly-capable data rather than opening on an empty table
  // (see `view` below).
  const [viewChoice, setViewChoice] = useState<ViewMode | null>(null)
  const { data, isLoading } = useUtilisationData(
    scheduleId,
    iteration,
    selectedWeek
  )

  const derived = useMemo(() => {
    if (!data) return null

    // daily_utilisation → per-asset 7-element [0..1] array.
    //
    // Keyed by (asset_type, asset_name), matching the `_asset_totals`
    // aggregation these rows are read alongside. Name alone is NOT unique
    // across asset types — a CIP name doubles as a resource-constraint name by
    // design (`constraint_type: "channels"` is how a CIP's capacity is set,
    // preprocess.rs `cip_capacity_override`) and nothing stops a kitchen and a
    // CIP sharing one either. Keying on the name alone let a later row
    // overwrite an earlier one, so one asset silently drew the other's daily
    // bars and capacity — wrong numbers with nothing on the console, unlike
    // the duplicate-React-key version of the same mistake.
    const assetKey = (type: string, name: string) => `${type}/${name}`
    const dailyByAsset = new Map<string, number[]>()
    // Same rows, but the actual hours-used / hours-available pair per day —
    // feeds the hover tooltip's "actual value" alongside the percentage.
    const dailyDetailByAsset = new Map<
      string,
      { used: number[]; cap: number[] }
    >()
    for (const row of data.daily_utilisation) {
      const key = assetKey(row.asset_type, row.asset_name)
      if (!dailyByAsset.has(key)) {
        dailyByAsset.set(key, new Array(7).fill(0))
      }
      // A kitchen is measured on CAPACITY — what its lines drew against its
      // rate — where the run reports it, not on how long it was busy: a
      // kitchen feeding one slow line all day is busy 100% of the day but
      // nowhere near full (client feedback 2026-10-06). Older runs carry
      // only busy time, which is what they keep showing.
      const capacityPct =
        row.asset_type === 'kitchen' ? row.capacity_used_pct : null
      const usesCapacity = capacityPct !== null && capacityPct !== undefined
      dailyByAsset.get(key)![row.day_of_week] =
        (usesCapacity ? capacityPct : row.utilisation_pct) / 100

      if (!dailyDetailByAsset.has(key)) {
        dailyDetailByAsset.set(key, {
          used: new Array(7).fill(0),
          cap: new Array(7).fill(0),
        })
      }
      const detail = dailyDetailByAsset.get(key)!
      detail.used[row.day_of_week] = usesCapacity
        ? (capacityPct / 100) * row.available_hrs
        : row.productive_hrs + row.cleaning_hrs
      detail.cap[row.day_of_week] = row.available_hrs
    }

    // constraint_utilisation → per-resource per-day over-capacity flag
    // Lines are excluded per spec: only crews and genuine constraint breaches get red rings.
    const constraintOverByDay = new Map<string, boolean[]>()
    for (const row of data.constraint_utilisation) {
      if (!constraintOverByDay.has(row.resource_name)) {
        constraintOverByDay.set(row.resource_name, new Array(7).fill(false))
      }
      if (row.max_pct_of_constraint > 100) {
        constraintOverByDay.get(row.resource_name)![row.day_of_week] = true
      }
    }

    // Kitchen capacity isn't one fixed number — it's a rate that varies by
    // whichever base material the kitchen is actually running (from
    // packagingRoutes, keyed by kitchen + base material). So instead of a
    // static left-hand label, work out per day which base material(s) ran
    // in each kitchen and what rate applied, for the hover tooltip.
    const kitchenDayNotes = new Map<string, string[]>()
    {
      const perKitchenDay = new Map<string, Map<string, string>[]>()
      for (const row of outputRows) {
        if (!row.kitchen || !row.base_material) continue
        const startHour = row.kitchen_start_time ?? 0
        if (!Number.isFinite(startHour)) continue
        const day = Math.floor(startHour / 24) % 7
        if (!perKitchenDay.has(row.kitchen)) {
          perKitchenDay.set(
            row.kitchen,
            Array.from({ length: 7 }, () => new Map<string, string>())
          )
        }
        const days = perKitchenDay.get(row.kitchen)!
        if (days[day].has(row.base_material)) continue
        const rate = packagingRoutes.find(
          (r) =>
            r.kitchen === row.kitchen && r.baseProductCode === row.base_material
        )?.kitchenRunRate
        days[day].set(
          row.base_material,
          rate ? `${row.base_material} @${rate}u/hr` : row.base_material
        )
      }
      perKitchenDay.forEach((days, kitchen) => {
        kitchenDayNotes.set(
          kitchen,
          days.map((m) => Array.from(m.values()).join(', '))
        )
      })
    }

    // Kitchens: each line's share of the day's capacity — its capacity-hours
    // that day over the kitchen's available hours, the same basis as the
    // kitchen's own figure, so the shares add up to it.
    const kitchenLineDayNotes = new Map<string, string[]>()
    for (const k of data.kitchen_hourly ?? []) {
      const cap =
        dailyDetailByAsset.get(assetKey('kitchen', k.asset_name))?.cap ??
        new Array(7).fill(24)
      kitchenLineDayNotes.set(
        k.asset_name,
        Array.from({ length: 7 }, (_, day) =>
          lineBreakdown(
            (k.by_line ?? []).map((l) => ({
              name: l.line_name,
              share:
                cap[day] > 0
                  ? l.used_fraction
                      .slice(day * 24, day * 24 + 24)
                      .reduce((sum, v) => sum + v, 0) / cap[day]
                  : 0,
            }))
          )
        )
      )
    }

    const buildAssetRow = (
      asset: (typeof data.asset_utilisation)[number],
      kind: 'kitchen' | 'line' | 'cip',
      group: string
    ): RowData => {
      const {
        asset_name: assetName,
        asset_type: assetType,
        utilisation_pct: busyPct,
        capacity_used_pct: capacityPct,
      } = asset
      // See the daily loop: kitchens on capacity used where reported.
      const utilPct =
        kind === 'kitchen' && capacityPct !== null && capacityPct !== undefined
          ? capacityPct
          : busyPct
      const key = assetKey(assetType, assetName)
      const daily7 = dailyByAsset.get(key) ?? new Array(7).fill(utilPct / 100)
      const detail = dailyDetailByAsset.get(key)
      // Available hours over the horizon — the time capacity those used hours
      // are measured against. (The kitchen RATE still isn't shown as a label:
      // it varies by base material, so it stays in the per-day tooltip.)
      const availHrs = (detail?.cap ?? []).reduce((sum, v) => sum + v, 0)
      return {
        name: assetName,
        sub: availHrs > 0 ? `cap ${Math.round(availHrs)}h` : undefined,
        u: dailyTo28(daily7),
        over: new Array(NB).fill(false), // kitchens/CIP never over-capacity
        avg: utilPct / 100,
        peak: Math.max(...daily7),
        kind,
        group,
        dayUsed: detail?.used ?? new Array(7).fill(0),
        dayCapacity: detail?.cap ?? new Array(7).fill(0),
        dayNote:
          kind === 'kitchen'
            ? joinDayNotes(
                kitchenDayNotes.get(assetName),
                kitchenLineDayNotes.get(assetName)
              )
            : undefined,
      }
    }

    const kitchenRows = data.asset_utilisation
      .filter((a) => a.asset_type === 'kitchen')
      .map((a) => buildAssetRow(a, 'kitchen', 'Kitchens'))

    // Lines come from the same server aggregate as kitchens (utilisation.rs
    // emits one `line` asset row per line per day: (production + cleaning) /
    // available hours), so both groups are measured the same way.
    const lineRows = data.asset_utilisation
      .filter((a) => a.asset_type === 'line')
      .map((a) => buildAssetRow(a, 'line', 'Lines'))

    const cipRows = data.asset_utilisation
      .filter((a) => a.asset_type === 'cip')
      .map((a) => buildAssetRow(a, 'cip', 'CIP stations'))

    // Crew rows: aggregate by name (kitchen/line sides — keep whichever side's
    // row has the higher utilisation, so hours_used/capacity stay consistent
    // with the utilisation_pct they're displayed alongside). Constraint
    // over-cap flags come from constraint_utilisation where available.
    const crewBest = new Map<string, (typeof data.crew_utilisation)[number]>()
    for (const row of data.crew_utilisation) {
      const cur = crewBest.get(row.resource_name)
      if (!cur || row.utilisation_pct > cur.utilisation_pct) {
        crewBest.set(row.resource_name, row)
      }
    }
    const crewRows: RowData[] = []
    crewBest.forEach((row, name) => {
      const utilFrac = row.utilisation_pct / 100
      const over7 =
        constraintOverByDay.get(name) ??
        new Array(7).fill(row.utilisation_pct > 100)
      // No daily breakdown exists for crews — the same overall totals are
      // repeated across every day, matching the already-uniform `u` bar.
      const totalCapacityHrs = row.capacity * row.horizon_hours
      crewRows.push({
        name,
        sub: `cap ${row.capacity}`,
        u: new Array(NB).fill(Math.min(1, utilFrac)),
        over: overTo28(over7),
        avg: Math.min(1, utilFrac),
        peak: utilFrac,
        kind: 'crew',
        group: 'Resources',
        dayUsed: new Array(7).fill(row.hours_used),
        dayCapacity: new Array(7).fill(totalCapacityHrs),
      })
    })

    const groups = [
      { label: 'Kitchens', icon: 'kitchen' as AssetKind, rows: kitchenRows },
      { label: 'Lines', icon: 'line' as AssetKind, rows: lineRows },
      // Crews and named constraints (sugar, steam, ...) alike, so not "crews".
      { label: 'Resources', icon: 'crew' as AssetKind, rows: crewRows },
      { label: 'CIP stations', icon: 'cip' as AssetKind, rows: cipRows },
    ].filter((g): g is HeatGroup => g.rows.length > 0)

    const allRows = [...kitchenRows, ...lineRows, ...crewRows, ...cipRows]
    const flatRows = [...allRows].sort((a, b) => b.avg - a.avg)

    const totalResources = allRows.length
    const avgUtilPct = totalResources
      ? Math.round(
          (allRows.reduce((s, r) => s + r.avg, 0) / totalResources) * 100
        )
      : 0
    const peakPct = totalResources
      ? Math.round(Math.max(...allRows.map((r) => r.peak)) * 100)
      : 0
    const overCapCount = allRows.filter((r) => r.over.some(Boolean)).length

    return {
      groups,
      flatRows,
      totalResources,
      avgUtilPct,
      peakPct,
      overCapCount,
    }
  }, [data])

  // Hourly table — built straight from the raw schedule (kitchenResources /
  // cipResources / outputRows+constraints), independent of the day-level
  // BaseUtilisation fetch above. This means it still renders for schedules
  // that ran via the legacy Synapse path (get_utilisation returns an empty
  // payload there — see SchedulerService's scheduler_utilisation_model.py).
  const hourlyGroups = useMemo<HourlyGroup[]>(() => {
    // Every group is drawn on one 0-168 grid: rows are shifted to match the
    // host's Gantt resources when it passes absolute rows alongside them.
    const rows = alignRowsToResources(outputRows, kitchenResources)
    const fromRows = deriveAssetIntervals(rows)
    // Prefer the host's Gantt resources where given — they carry downtime,
    // which rows do not — and fall back to the rows for any group it omits.
    // Capacity used per hour where the run reports it (see the heat map);
    // busy time otherwise. The availability (`cap`) still comes from the
    // asset's downtime either way.
    const capacityByKitchen = new Map(
      (data?.kitchen_hourly ?? []).map((k) => [k.asset_name, k])
    )
    const kitchenHourly = (
      kitchenResources.length ? kitchenResources : fromRows.kitchens
    ).map((r) => {
      const row = buildAssetHourlyRow(r, 'kitchen', 'Kitchens')
      const capacity = capacityByKitchen.get(r.name)
      if (!capacity) return row
      return {
        ...row,
        used: row.used.map((_, h) => capacity.used_fraction[h] ?? 0),
        // Which lines drew it that hour, largest first.
        hourNote: row.used.map((_, h) =>
          lineBreakdown(
            (capacity.by_line ?? []).map((l) => ({
              name: l.line_name,
              share: l.used_fraction[h] ?? 0,
            }))
          )
        ),
      }
    })
    const lineHourly = fromRows.lines.map((r) =>
      buildAssetHourlyRow(r, 'line', 'Lines')
    )
    const cipHourly = (cipResources.length ? cipResources : fromRows.cips).map(
      (r) => buildAssetHourlyRow(r, 'cip', 'CIP stations')
    )

    const capByResource = new Map<string, { amount: number; uom: string }>()
    for (const c of constraints) {
      if (c?.ResourceConstraint) {
        capByResource.set(String(c.ResourceConstraint), {
          amount: Number(c.ConstraintAmount) || 0,
          uom: c.UOM || '',
        })
      }
    }
    const resourceHourly: HourlyResourceRow[] =
      outputRows.length || constraints.length
        ? generateConstraintRows(rows, constraints, HOURS).map((row) => {
            const meta = capByResource.get(row.resource)
            const used: number[] = []
            for (let h = 0; h < HOURS; h++) {
              used.push(Number(row[`${row.resource}-${h}`]) || 0)
            }
            const capVal = meta?.amount ?? 0
            return {
              name: row.resource,
              sub: meta
                ? `cap ${capVal}${meta.uom ? ' ' + meta.uom : ''}`
                : undefined,
              kind: 'crew' as AssetKind,
              group: 'Resources',
              used,
              cap: new Array(HOURS).fill(capVal),
            }
          })
        : []

    return (
      [
        // Same groups, same order as the heat map.
        { label: 'Kitchens', icon: 'kitchen', rows: kitchenHourly },
        { label: 'Lines', icon: 'line', rows: lineHourly },
        { label: 'Resources', icon: 'crew', rows: resourceHourly },
        { label: 'CIP stations', icon: 'cip', rows: cipHourly },
      ] as HourlyGroup[]
    ).filter((g) => g.rows.length > 0)
  }, [kitchenResources, cipResources, outputRows, constraints, data])

  const hourlyResourceCount = hourlyGroups.reduce(
    (s, g) => s + g.rows.length,
    0
  )
  const hasHourlyData = hourlyResourceCount > 0
  const view: ViewMode = viewChoice ?? (hasHourlyData ? 'hourly' : 'both')

  if (isLoading && !hasHourlyData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 240,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    )
  }

  if ((!data || !derived || derived.totalResources === 0) && !hasHourlyData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 240,
        }}
      >
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          No utilisation data available.
        </Typography>
      </Box>
    )
  }

  const safeDerived = derived ?? {
    groups: [] as HeatGroup[],
    flatRows: [] as RowData[],
    totalResources: 0,
    avgUtilPct: 0,
    peakPct: 0,
    overCapCount: 0,
  }
  const {
    groups,
    flatRows,
    totalResources,
    avgUtilPct,
    peakPct,
    overCapCount,
  } = safeDerived
  const avgTone = avgUtilPct >= 70 ? TOKENS.stateComplete : TOKENS.stateRunning

  const headerMetrics: Array<{
    label: string
    value: number | string
    unit?: string
    tone?: string
  }> = [
    {
      label: 'Resources',
      value: Math.max(totalResources, hourlyResourceCount),
    },
    { label: 'Avg util', value: avgUtilPct, unit: '%', tone: avgTone },
    { label: 'Peak', value: peakPct, unit: '%' },
    {
      label: 'Over cap',
      value: overCapCount,
      tone: overCapCount > 0 ? TOKENS.ganttDowntime : TOKENS.stateComplete,
    },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: TOKENS.surface0,
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${TOKENS.line200}`,
          px: '18px',
          py: '11px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}
      >
        <GridViewIcon
          sx={{ fontSize: 19, color: TOKENS.stateComplete, flexShrink: 0 }}
        />
        <Box
          sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.18 }}
        >
          <Typography sx={{ ...TYPE.h2 }}>Resource usage</Typography>
          <Typography sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>
            {view === 'hourly'
              ? 'Every resource across the week · 1-hour resolution'
              : 'Every resource across the week · 6-hour buckets'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {headerMetrics.map((m, i) => (
            <Box
              key={m.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                pr: i < headerMetrics.length - 1 ? '18px' : 0,
                borderRight:
                  i < headerMetrics.length - 1
                    ? `1px solid ${TOKENS.line200}`
                    : 'none',
                mr: i < headerMetrics.length - 1 ? '18px' : 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: TOKENS.ink400,
                }}
              >
                {m.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: m.tone ?? TOKENS.ink900,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.value}
                {m.unit && (
                  <Box
                    component="span"
                    sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
                  >
                    {m.unit}
                  </Box>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
        <ViewToggle value={view} onChange={setViewChoice} />
      </Box>

      {/* Scrollable body */}
      <Box sx={{ overflow: 'auto', px: '18px', pt: '16px', pb: '28px' }}>
        {/* ── Heatmap ── */}
        {(view === 'heat' || view === 'both') && (
          <Box sx={{ mb: view === 'both' ? '26px' : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '12px' }}>
              <Typography sx={{ flex: 1, ...TYPE.sectionLabel }}>
                Usage heatmap
              </Typography>
              <HeatScaleLegend />
            </Box>
            <HeatDayHeader />
            {groups.map((g) => {
              const GroupIcon = KIND_ICON[g.icon]
              return (
                <Box key={g.label} sx={{ mb: '12px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      my: '5px',
                    }}
                  >
                    <GroupIcon
                      sx={{ fontSize: 14, color: TOKENS.stateComplete }}
                    />
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: TOKENS.ink500,
                      }}
                    >
                      {g.label}
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: TOKENS.line100 }} />
                  </Box>
                  {g.rows.map((r) => (
                    <HeatRow
                      key={r.name}
                      name={r.name}
                      sub={r.sub}
                      u={r.u}
                      over={r.over}
                      dayUsed={r.dayUsed}
                      dayCapacity={r.dayCapacity}
                      dayNote={r.dayNote}
                    />
                  ))}
                </Box>
              )
            })}
          </Box>
        )}

        {/* ── Bars ── */}
        {(view === 'bars' || view === 'both') && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
              <Typography sx={{ flex: 1, ...TYPE.sectionLabel }}>
                Utilisation by resource
              </Typography>
              <Typography sx={{ fontSize: 10, color: TOKENS.ink400 }}>
                sorted by average · tick = peak
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 22,
                pl: '38px',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  width: 150,
                  fontSize: 8.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: TOKENS.ink300,
                }}
              >
                Resource
              </Typography>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 8.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: TOKENS.ink300,
                }}
              >
                Average utilisation
              </Typography>
              <Box sx={{ width: 46 }} />
              <Box sx={{ width: 70 }} />
              <Box sx={{ width: 72 }} />
            </Box>
            {/* Keyed by group AND name, not name alone. This is the only
                place the three groups are rendered as ONE flat list, and a
                name is unique only WITHIN a group: `_asset_totals` aggregates
                assets by (asset_type, asset_name) and `crewBest` dedups crews
                by name, but nothing stops one name being both. It routinely
                is — a CIP name doubles as a resource-constraint name, which is
                how `constraint_type: "channels"` sets a CIP's capacity
                (preprocess.rs `cip_capacity_override`), so a drawn CIP1 lands
                in crewRows while the CIP1 asset lands in cipRows. The grouped
                heatmap above is safe because it maps each group separately. */}
            {flatRows.map((r) => (
              <BarRow key={`${r.group}/${r.name}`} row={r} />
            ))}
          </Box>
        )}

        {/* ── Hourly table ── */}
        {view === 'hourly' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
              <Typography sx={{ flex: 1, ...TYPE.sectionLabel }}>
                Hourly usage — every resource, every hour
              </Typography>
              <HeatScaleLegend overLabel="at / over capacity" />
            </Box>
            {hasHourlyData ? (
              <HourlyTable groups={hourlyGroups} />
            ) : (
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: TOKENS.ink400,
                  py: 3,
                  textAlign: 'center',
                }}
              >
                No hourly schedule data available.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
